/** Reordering and hiding Overview cards — the client-side half, in one testable place.
 *
 *  The server owns the catalogue, the role filter and the three rules a saved layout
 *  must obey (see the section note in backend/server.py). These are the operations the
 *  editor performs on the resolved list it was handed.
 *
 *  Reordering is by BUTTON, not by drag: a move handled with real <button> elements is
 *  keyboard-operable because that is what a button is, where a drag surface needs a
 *  parallel keyboard implementation that is never tested and quietly rots. There is no
 *  drag implementation at all, deliberately.
 */

/** Move one card up (delta -1) or down (delta +1).
 *
 *  Returns the SAME array when the move is impossible — at either end, or for an id that
 *  is not in the list. A caller can therefore compare by identity to know whether
 *  anything changed, and a button at the end of the list cannot produce a save that
 *  changes nothing.
 */
export function moveCard(order, id, delta) {
  if (!Array.isArray(order)) return order;
  const from = order.indexOf(id);
  if (from < 0) return order;
  const to = from + delta;
  if (to < 0 || to >= order.length) return order;
  const next = [...order];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

export function canMoveUp(order, id) {
  return Array.isArray(order) && order.indexOf(id) > 0;
}

export function canMoveDown(order, id) {
  if (!Array.isArray(order)) return false;
  const at = order.indexOf(id);
  return at >= 0 && at < order.length - 1;
}

/** 1-based position, for the "3 of 12" a screen reader needs to make sense of a move. */
export function cardPosition(order, id) {
  const at = Array.isArray(order) ? order.indexOf(id) : -1;
  return at < 0 ? null : at + 1;
}

/** Hide a shown card, or show a hidden one. */
export function toggleHidden(hidden, id) {
  const list = Array.isArray(hidden) ? hidden : [];
  return list.includes(id) ? list.filter((h) => h !== id) : [...list, id];
}

/** The cards that render, in order. */
export function visibleCards(cards) {
  return (Array.isArray(cards) ? cards : []).filter((c) => c && !c.hidden);
}

/** The cards that do not — listed in the editor so a hidden card can be found again.
 *  Without this, hiding a card is a one-way door. */
export function hiddenCardList(cards) {
  return (Array.isArray(cards) ? cards : []).filter((c) => c && c.hidden);
}

/** Apply an order and a hidden list to the resolved cards, for optimistic rendering
 *  while a save is in flight. Unknown ids in `order` are dropped, matching the server. */
export function applyLayout(cards, order, hidden) {
  const byId = new Map((Array.isArray(cards) ? cards : []).map((c) => [c.id, c]));
  const hiddenSet = new Set(Array.isArray(hidden) ? hidden : []);
  const out = [];
  (Array.isArray(order) ? order : []).forEach((id) => {
    const card = byId.get(id);
    if (card && !out.some((c) => c.id === id)) {
      out.push({ ...card, hidden: hiddenSet.has(id) });
    }
  });
  // Anything the order did not mention keeps the server's position at the end, visible
  // unless hidden — the same rule the server applies to a card added since the layout
  // was saved.
  byId.forEach((card, id) => {
    if (!out.some((c) => c.id === id)) out.push({ ...card, hidden: hiddenSet.has(id) });
  });
  return out;
}
